import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUserRoles } from '../../features/auth/authSlice';
import { useListInvestmentPlansQuery, useDeleteInvestmentPlanMutation } from '../../features/investmentPlans/investmentPlansApi';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import PlanCard from '../../components/plans/PlanCard';
import PlanDetailModal from '../../components/plans/PlanDetailModal';
import PlanFormModal from '../../components/plans/PlanFormModal';
import EmptyState from '../../components/ui/EmptyState';
import { LayersIcon } from '../../components/ui/icons';
import { SkeletonCardGrid } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import { authErrorMessage } from '../../lib/authErrorMessage';

/**
 * Real-backend Investment Plans catalog — GET /investor/investment-plans, live for
 * any authenticated user. Create/Edit/Delete require manage-investment-
 * plans, held by both admin and super-admin (unlike Admin Management,
 * which is super-admin only), so both roles see the full CRUD UI here.
 */
export default function Plans() {
  const roles = useSelector(selectUserRoles);
  const canManage = roles.includes('admin') || roles.includes('super-admin');

  const { data, isLoading, isFetching } = useListInvestmentPlansQuery();
  const [deletePlan, { isLoading: isDeleting }] = useDeleteInvestmentPlanMutation();
  const toast = useToast();

  const [viewing, setViewing] = useState(null);
  // undefined = form closed, null = create mode, a plan object = edit mode.
  const [formTarget, setFormTarget] = useState(undefined);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const plans = data ?? [];

  const runDelete = async () => {
    try {
      await deletePlan(deleteTarget.id).unwrap();
      toast.push({ tone: 'success', message: `${deleteTarget.name} was deleted.` });
      setDeleteTarget(null);
      setViewing(null);
    } catch (err) {
      toast.push({
        tone: 'danger',
        title: 'Delete failed',
        message: authErrorMessage(err, { notFound: 'This plan no longer exists.' }),
      });
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-foreground">Investment plans</h1>
            <Badge tone="success">Live</Badge>
          </div>
          <p className="text-foreground-muted text-sm mt-1">
            Investor subscriptions aren't wired up on the backend yet.
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setFormTarget(null)} className="self-start sm:self-auto">
            Add plan
          </Button>
        )}
      </div>

      <Card>
        <CardHeader title="Catalog" subtitle={isFetching && !isLoading ? 'Refreshing…' : undefined} />

        {isLoading && <SkeletonCardGrid count={6} />}
        {!isLoading && plans.length === 0 && (
          <EmptyState icon={LayersIcon} title="No plans yet" body="Add a plan to start offering it to investors." />
        )}

        {!isLoading && plans.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} onView={setViewing} />
            ))}
          </div>
        )}
      </Card>

      <PlanDetailModal
        plan={viewing}
        onClose={() => setViewing(null)}
        canManage={canManage}
        onEdit={(plan) => {
          setViewing(null);
          setFormTarget(plan);
        }}
        onDelete={(plan) => setDeleteTarget(plan)}
      />

      {canManage && <PlanFormModal open={formTarget !== undefined} onClose={() => setFormTarget(undefined)} plan={formTarget} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={runDelete}
        isLoading={isDeleting}
        title="Delete plan"
        confirmLabel="Delete"
        message={`This permanently deletes "${deleteTarget?.name}" and its photo. This can't be undone.`}
      />
    </div>
  );
}
